import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { authedApiJsonOrThrow, authedFetchJson } from '../../utils/api';
import { navigate } from '../../router/router';
import { reportHandledError } from '../../monitoring/uiErrorSink';

// ============================================================================
// Types
// ============================================================================
export type OnboardingSlide = {
  position: number;
  image_url: string | null;
};

type SlideData = {
  titleKey: string;
  textKey: string;
  imagePosition?: string;
  imageStyle?: Record<string, string>;
};

// ============================================================================
// Slide definitions
// ============================================================================
const SLIDES: SlideData[] = [
  { titleKey: 'onboarding.step1.title', textKey: 'onboarding.step1.text', imagePosition: 'top center' },
  {
    titleKey: 'onboarding.step2.title', textKey: 'onboarding.step2.text',
    imagePosition: 'center top',
    imageStyle: { top: '-15%', height: '115%' },
  },
  { titleKey: 'onboarding.step3.title', textKey: 'onboarding.step3.text', imagePosition: 'top center' },
  { titleKey: 'onboarding.step4.title', textKey: 'onboarding.step4.text', imagePosition: 'top center' },
];

const FALLBACK_TITLES = [
  'Pick the one you want.',
  'Tell her what to do.',
  'Unlock Full Access.',
  'Try it for free.',
];

const FALLBACK_TEXTS = [
  'Each one reacts differently.\nIt\'s up to you how far it goes.',
  'Tap a command.\nShe reacts instantly.\nYour words control what happens next.',
  'Exclusive limited-time offer.\nUnlimited commands.\nFaster reactions.\nMore intense scenarios.',
  'Complete quick daily tasks and earn free credits every day.',
];

const PREMIUM_SLIDE_INDEX = 2;
const TASKS_SLIDE_INDEX = 3;
const TIMER_DURATION_MS = 5 * 60 * 1000;

// ============================================================================
// Timer hook
// ============================================================================
function useCountdown(startMs: number | null) {
  const [remaining, setRemaining] = useState(TIMER_DURATION_MS);

  useEffect(() => {
    if (!startMs) return;
    const tick = () => {
      const left = Math.max(0, TIMER_DURATION_MS - (Date.now() - startMs));
      setRemaining(left);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  const expired = remaining <= 0;
  const display = `${mins}:${secs.toString().padStart(2, '0')}`;

  return { remaining, mins, secs, expired, display };
}

// ============================================================================
// OnboardingSheet Component
// ============================================================================
export function OnboardingSheet(props: {
  open: boolean;
  onClose: () => void;
  onOpenPremium?: () => void;
  slides?: OnboardingSlide[];
  initialSlide?: number;
}) {
  const { open, onClose, onOpenPremium, slides, initialSlide } = props;
  const [slideIndex, setSlideIndex] = useState(initialSlide || 0);
  const [closing, setClosing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef(0);
  const touchDeltaRef = useRef(0);
  const timerStartRef = useRef<number | null>(null);

  const boot = bootOnce();
  const i18n = boot.i18n;
  const { t } = useI18n(i18n);

  // Start timer on first open
  if (open && !timerStartRef.current) {
    timerStartRef.current = Date.now();
  }

  const countdown = useCountdown(open ? timerStartRef.current : null);

  const reducedMotion = useMemo(() => {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    } catch {
      return false;
    }
  }, []);

  const close = useCallback(() => {
    if (closing) return;
    if (reducedMotion) {
      onClose();
      return;
    }
    setClosing(true);
    setTimeout(() => onClose(), 400);
  }, [closing, onClose, reducedMotion]);

  useEffect(() => {
    if (!open) return;
    setSlideIndex(initialSlide || 0);
    setClosing(false);
    setCompleting(false);
  }, [open, initialSlide]);

  // Touch swipe — blocked forward on premium slide
  const onTouchStart = useCallback((e: TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? 0;
    touchDeltaRef.current = 0;
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    const x = e.touches[0]?.clientX ?? 0;
    touchDeltaRef.current = x - touchStartXRef.current;
  }, []);

  const onTouchEnd = useCallback(() => {
    const delta = touchDeltaRef.current;
    const threshold = 50;
    if (delta < -threshold && slideIndex < SLIDES.length - 1) {
      // Block forward swipe from premium slide — only "Maybe Later" advances
      if (slideIndex === PREMIUM_SLIDE_INDEX) {
        touchDeltaRef.current = 0;
        return;
      }
      setSlideIndex((i) => i + 1);
    } else if (delta > threshold && slideIndex > 0) {
      setSlideIndex((i) => i - 1);
    }
    touchDeltaRef.current = 0;
  }, [slideIndex]);

  const markComplete = useCallback(async () => {
    try {
      await authedApiJsonOrThrow(boot, '/api/user/onboarding-complete', { method: 'POST' });
    } catch (e) {
      reportHandledError({
        err: e,
        code: 'onboarding_complete_failed',
        userMessage: 'Failed to update onboarding',
        context: { op: 'onboarding_complete' },
        severity: 'warning',
      });
    }
  }, [boot]);

  const completeAndStay = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    await markComplete();
    close();
  }, [completing, markComplete, close]);

  const completeAndGoTasks = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    await markComplete();
    close();
    navigate({ name: 'tasks', from: 'storefront' });
  }, [completing, markComplete, close]);

  const pauseAndOpenPremium = useCallback(() => {
    if (completing) return;
    onOpenPremium?.();
  }, [completing, onOpenPremium]);

  if (!open) return null;

  const currentSlide = SLIDES[slideIndex];
  const serverImage = slides?.[slideIndex]?.image_url || null;
  const hasBackground = !!serverImage;

  // Dot click handler — blocks navigating to slide 4 from slide 3
  const handleDotClick = (idx: number) => {
    if (slideIndex === PREMIUM_SLIDE_INDEX && idx === TASKS_SLIDE_INDEX) return;
    setSlideIndex(idx);
  };

  const renderButtons = () => {
    if (slideIndex === PREMIUM_SLIDE_INDEX) {
      return (
        <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={pauseAndOpenPremium}
            disabled={completing}
            style={{
              width: '100%', height: 56, borderRadius: 28, border: 'none',
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: '#000', fontSize: 18, fontWeight: 700,
              cursor: completing ? 'wait' : 'pointer',
              opacity: completing ? 0.7 : 1,
              boxShadow: '0 8px 24px rgba(255,215,0,0.3)',
            }}
          >
            {t('onboarding.premium_cta', 'Get Premium Now')}
          </button>
          <button
            onClick={() => setSlideIndex(TASKS_SLIDE_INDEX)}
            disabled={completing}
            style={{
              background: 'none', border: 'none', padding: '8px 0',
              color: 'rgba(255,255,255,0.35)', fontSize: 13,
              cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            {t('onboarding.skip', 'Continue with limits')}
          </button>
        </div>
      );
    }

    if (slideIndex === TASKS_SLIDE_INDEX) {
      return (
        <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={completeAndGoTasks}
            disabled={completing}
            style={{
              width: '100%', height: 56, borderRadius: 28, border: 'none',
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: '#000', fontSize: 18, fontWeight: 700,
              cursor: completing ? 'wait' : 'pointer',
              opacity: completing ? 0.7 : 1,
              boxShadow: '0 8px 24px rgba(255,215,0,0.3)',
            }}
          >
            {t('onboarding.tasks_cta', 'Get Free Credits')}
          </button>
          <button
            onClick={completeAndStay}
            disabled={completing}
            style={{
              width: '100%', height: 48, borderRadius: 24,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('onboarding.close', 'Start Exploring')}
          </button>
        </div>
      );
    }

    // Slides 1-2: Next + "Don't show again"
    return (
      <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
        <button
          onClick={() => setSlideIndex((i) => i + 1)}
          style={{
            width: '100%', height: 56, borderRadius: 28,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.9)', fontSize: 16, fontWeight: 600,
            cursor: 'pointer', transition: 'background 200ms',
          }}
        >
          {t('common.next', 'Next')}
        </button>
        <button
          onClick={completeAndStay}
          disabled={completing}
          style={{
            background: 'none', border: 'none', padding: '8px 0',
            color: 'rgba(255,255,255,0.35)', fontSize: 13,
            cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          {t('onboarding.dont_show', "Don't show again")}
        </button>
      </div>
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome Tutorial"
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: '#0d0505',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        animation: closing
          ? 'onbFadeOut 300ms ease-in forwards'
          : 'onbFadeIn 300ms ease-out',
      }}
      onAnimationEnd={(e) => {
        if (!closing) return;
        if (e.currentTarget !== e.target) return;
        onClose();
      }}
      onTouchStart={onTouchStart as any}
      onTouchMove={(e) => {
        e.preventDefault();
        onTouchMove(e as any);
      }}
      onTouchEnd={onTouchEnd as any}
    >
        {/* Background image (when admin-uploaded) */}
        {hasBackground && (
          <>
            <img
              key={`bg-${slideIndex}`}
              src={serverImage!}
              alt=""
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: currentSlide.imagePosition || 'top center',
                animation: reducedMotion ? 'none' : 'slideIn 300ms ease-out',
                ...(currentSlide.imageStyle || {}),
              }}
            />
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0) 20%, rgba(13,5,5,0.15) 35%, rgba(13,5,5,0.5) 48%, rgba(13,5,5,0.85) 60%, #0d0505 72%)',
            }} />
          </>
        )}

        {/* Glow effect (only when no background image) */}
        {!hasBackground && (
          <div
            style={{
              position: 'absolute', top: '-80px', left: '50%',
              transform: 'translateX(-50%)', width: '100%', height: '250px',
              background: 'radial-gradient(closest-side, rgba(220,20,60,0.3), transparent)',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Spacer pushes content to bottom */}
        <div style={{ flex: 1 }} />

        {/* Content pinned to bottom */}
        <div
          key={slideIndex}
          style={{
            position: 'relative', zIndex: 1,
            textAlign: 'center', padding: '0 24px',
            animation: reducedMotion ? 'none' : 'slideIn 300ms ease-out',
          }}
        >
          {slideIndex === PREMIUM_SLIDE_INDEX && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 14px', borderRadius: 16,
              background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,165,0,0.15))',
              border: '1px solid rgba(255,215,0,0.35)',
              marginBottom: 10,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#FFD700' }}>+200% value</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>only now</span>
            </div>
          )}
          <h2
            style={{
              fontSize: 28, fontWeight: 700,
              color: 'rgba(255,255,255,0.95)',
              margin: '0 0 12px 0', lineHeight: 1.3,
            }}
          >
            {t(currentSlide.titleKey, FALLBACK_TITLES[slideIndex])}
          </h2>

          <p
            style={{
              fontSize: 16, color: 'rgba(255,255,255,0.7)',
              margin: '0 auto 16px', lineHeight: 1.5, maxWidth: 300,
              whiteSpace: 'pre-line',
            }}
          >
            {t(currentSlide.textKey, FALLBACK_TEXTS[slideIndex])}
          </p>

          {/* Countdown timer on premium slide */}
          {slideIndex === PREMIUM_SLIDE_INDEX && !countdown.expired && (
            <div
              style={{
                marginBottom: 16, padding: '8px 20px', borderRadius: 20,
                background: 'rgba(255,215,0,0.12)',
                border: '1px solid rgba(255,215,0,0.25)',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                {t('onboarding.offer_expires', 'Offer expires in')}
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#FFD700', fontVariantNumeric: 'tabular-nums' }}>
                {countdown.display}
              </span>
            </div>
          )}
          {slideIndex === PREMIUM_SLIDE_INDEX && countdown.expired && (
            <div
              style={{
                marginBottom: 16, padding: '8px 20px', borderRadius: 20,
                background: 'rgba(255,60,60,0.1)',
                border: '1px solid rgba(255,60,60,0.2)',
              }}
            >
              <span style={{ fontSize: 13, color: 'rgba(255,100,100,0.8)' }}>
                {t('onboarding.offer_expired', 'Special offer expired')}
              </span>
            </div>
          )}

          {/* Dot indicators */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 4 }}>
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => handleDotClick(idx)}
                style={{
                  width: idx === slideIndex ? 24 : 10, height: 10,
                  borderRadius: 5, border: 'none',
                  background: idx === slideIndex
                    ? 'linear-gradient(135deg, #FFD700, #FFA500)'
                    : 'rgba(255,255,255,0.3)',
                  cursor: (slideIndex === PREMIUM_SLIDE_INDEX && idx === TASKS_SLIDE_INDEX) ? 'default' : 'pointer',
                  transition: 'all 200ms ease', padding: 0,
                  opacity: (slideIndex === PREMIUM_SLIDE_INDEX && idx === TASKS_SLIDE_INDEX) ? 0.3 : 1,
                }}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {renderButtons()}
        </div>

        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes onbFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes onbFadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        `}</style>
    </div>
  );
}

// ============================================================================
// Hook to manage onboarding state
// ============================================================================
export function useOnboardingState() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checked, setChecked] = useState(false);
  const [slides, setSlides] = useState<OnboardingSlide[]>([]);
  const [resumeSlide, setResumeSlide] = useState(0);
  const pausedRef = useRef(false);
  const boot = bootOnce();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authedFetchJson<{ completed: boolean; slides?: OnboardingSlide[] }>(boot, '/api/user/onboarding');
        if (cancelled) return;
        if (r.ok && r.data && typeof r.data.completed === 'boolean') {
          if (!r.data.completed) {
            setShowOnboarding(true);
          }
          if (Array.isArray(r.data.slides)) {
            setSlides(r.data.slides);
          }
        }
      } catch (e) {
        reportHandledError({
          err: e,
          code: 'onboarding_check_failed',
          userMessage: 'Failed to check onboarding',
          context: { op: 'check_onboarding' },
          severity: 'warning',
        });
      }
      if (!cancelled) setChecked(true);
    })();
    return () => { cancelled = true; };
  }, [boot]);

  const closeOnboarding = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  const pauseOnboarding = useCallback(() => {
    pausedRef.current = true;
    setShowOnboarding(false);
  }, []);

  const resumeOnboarding = useCallback((atSlide: number) => {
    pausedRef.current = false;
    setResumeSlide(atSlide);
    setShowOnboarding(true);
  }, []);

  const completeOnboarding = useCallback(() => {
    pausedRef.current = false;
    setShowOnboarding(false);
  }, []);

  return {
    showOnboarding, closeOnboarding, checked, slides,
    resumeSlide, pauseOnboarding, resumeOnboarding, completeOnboarding,
    isPaused: pausedRef,
  };
}
